import { Modal } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { FC, ReactNode } from "react";
import styled from "styled-components/macro";
import { DeviceType } from "../../../utils/hooks/useDeviceHook";

interface TableModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  titleColor?: string;
  message?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  deviceType: DeviceType;
  maxHeight?: string;
}

const getWidth = (deviceType: DeviceType): string => {
  if (deviceType === "mobile") return "95%";
  if (deviceType === "tablet") return "580px";
  return "650px";
};

const Wrapper = styled.div<{ $deviceType: DeviceType; $maxHeight?: string }>`
  display: flex;
  flex-direction: column;
  width: ${({ $deviceType }) => getWidth($deviceType)};
  max-height: ${({ $maxHeight }) => $maxHeight || "80vh"};
  background: white;
  border-radius: 8px;
  overflow: hidden;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px;
  border-bottom: 1px solid #dbdee6;
`;

const Title = styled.h2<{ $color?: string }>`
  margin: 0;
  font-size: 18px;
  font-weight: 700;
  color: ${({ $color }) => $color || "#333"};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #666;
  &:hover {
    color: #333;
  }
`;

const Message = styled.div`
  padding: 16px 24px;
  font-size: 14px;
  line-height: 22px;
  color: #333;
`;

const Body = styled.div`
  flex: 1;
  overflow-y: auto;
  ::-webkit-scrollbar {
    width: 8px;
  }
  ::-webkit-scrollbar-thumb {
    border-radius: 4px;
    background-color: rgba(0, 0, 0, 0.3);
  }
`;

const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
  padding: 20px 24px;
`;

export const TableModal: FC<TableModalProps> = ({
  open,
  onClose,
  title,
  titleColor,
  message,
  children,
  footer,
  deviceType,
  maxHeight,
}) => (
  <Modal
    open={open}
    onClose={onClose}
    style={{ display: "flex", alignItems: "center", justifyContent: "center" }}
  >
    <Wrapper $deviceType={deviceType} $maxHeight={maxHeight}>
      <Header>
        <Title $color={titleColor}>{title}</Title>
        <CloseButton onClick={onClose}>
          <CloseIcon />
        </CloseButton>
      </Header>
      {message && <Message>{message}</Message>}
      <Body>{children}</Body>
      {footer && <Footer>{footer}</Footer>}
    </Wrapper>
  </Modal>
);
